/**
 * Translation Quality Assurance Checker
 * Checks for common translation issues
 */

export interface QAIssue {
  type: 'error' | 'warning' | 'info'
  category: 'numbers' | 'spaces' | 'punctuation' | 'length' | 'tags' | 'consistency'
  message: string
  position?: number
}

export interface QAResult {
  passed: boolean
  issues: QAIssue[]
}

export class QAChecker {
  /**
   * Run all QA checks on a segment pair
   */
  static checkSegment(source: string, target: string): QAResult {
    const issues: QAIssue[] = []

    // Skip if target is empty
    if (!target || target.trim() === '') {
      return { passed: true, issues: [] }
    }

    // Run all checks
    issues.push(...this.checkNumbers(source, target))
    issues.push(...this.checkSpaces(source, target))
    issues.push(...this.checkPunctuation(source, target))
    issues.push(...this.checkLength(source, target))
    issues.push(...this.checkTags(source, target))
    issues.push(...this.checkConsistency(source, target))

    return {
      passed: issues.filter(i => i.type === 'error').length === 0,
      issues
    }
  }

  /**
   * Check number consistency
   * Numbers in source should match numbers in target
   */
  private static checkNumbers(source: string, target: string): QAIssue[] {
    const issues: QAIssue[] = []

    // Extract all numbers (including decimals, thousands separators)
    const numberRegex = /\d+([,\.]\d+)*/g
    const sourceNumbers = source.match(numberRegex) || []
    const targetNumbers = target.match(numberRegex) || []

    // Normalize numbers for comparison (remove separators)
    const normalizeNumber = (num: string) => num.replace(/[,\.]/g, '')
    const normalizedSourceNumbers = sourceNumbers.map(normalizeNumber).sort()
    const normalizedTargetNumbers = targetNumbers.map(normalizeNumber).sort()

    // Check if all numbers are present
    if (normalizedSourceNumbers.length !== normalizedTargetNumbers.length) {
      issues.push({
        type: 'error',
        category: 'numbers',
        message: `Number mismatch: Source has ${sourceNumbers.length} number(s) [${sourceNumbers.join(', ')}], target has ${targetNumbers.length} [${targetNumbers.join(', ')}]`
      })
      return issues
    }

    // Check if numbers match (order independent)
    for (let i = 0; i < normalizedSourceNumbers.length; i++) {
      if (normalizedSourceNumbers[i] !== normalizedTargetNumbers[i]) {
        issues.push({
          type: 'error',
          category: 'numbers',
          message: `Number mismatch: Expected "${sourceNumbers[i]}" in target but found "${targetNumbers[i] || 'missing'}"`
        })
      }
    }

    return issues
  }

  /**
   * Check leading/trailing spaces
   */
  private static checkSpaces(source: string, target: string): QAIssue[] {
    const issues: QAIssue[] = []

    // Leading spaces
    const sourceLeadingSpaces = source.match(/^\s+/)?.[0].length || 0
    const targetLeadingSpaces = target.match(/^\s+/)?.[0].length || 0

    if (sourceLeadingSpaces !== targetLeadingSpaces) {
      issues.push({
        type: 'warning',
        category: 'spaces',
        message: `Leading spaces mismatch: Source has ${sourceLeadingSpaces}, target has ${targetLeadingSpaces}`
      })
    }

    // Trailing spaces
    const sourceTrailingSpaces = source.match(/\s+$/)?.[0].length || 0
    const targetTrailingSpaces = target.match(/\s+$/)?.[0].length || 0

    if (sourceTrailingSpaces !== targetTrailingSpaces) {
      issues.push({
        type: 'warning',
        category: 'spaces',
        message: `Trailing spaces mismatch: Source has ${sourceTrailingSpaces}, target has ${targetTrailingSpaces}`
      })
    }

    // Multiple consecutive spaces
    if (/\s{2,}/.test(target)) {
      issues.push({
        type: 'warning',
        category: 'spaces',
        message: 'Multiple consecutive spaces found in target'
      })
    }

    return issues
  }

  /**
   * Check punctuation consistency
   */
  private static checkPunctuation(source: string, target: string): QAIssue[] {
    const issues: QAIssue[] = []

    // Check ending punctuation
    const sourceEnding = source.trim().slice(-1)
    const targetEnding = target.trim().slice(-1)

    const punctuationMarks = ['.', '!', '?', ':', ';', ',']
    const sourceHasPunctuation = punctuationMarks.includes(sourceEnding)
    const targetHasPunctuation = punctuationMarks.includes(targetEnding)

    if (sourceHasPunctuation && !targetHasPunctuation) {
      issues.push({
        type: 'warning',
        category: 'punctuation',
        message: `Source ends with "${sourceEnding}" but target doesn't end with punctuation`
      })
    } else if (!sourceHasPunctuation && targetHasPunctuation) {
      issues.push({
        type: 'warning',
        category: 'punctuation',
        message: `Target ends with "${targetEnding}" but source doesn't end with punctuation`
      })
    } else if (sourceEnding === '.' && targetEnding !== '.') {
      issues.push({
        type: 'info',
        category: 'punctuation',
        message: `Source ends with period but target ends with "${targetEnding}"`
      })
    }

    return issues
  }

  /**
   * Check translation length (relative to source)
   */
  private static checkLength(source: string, target: string): QAIssue[] {
    const issues: QAIssue[] = []

    const sourceLength = source.trim().length
    const targetLength = target.trim().length

    if (sourceLength === 0) return issues

    const ratio = targetLength / sourceLength

    // Warn if translation is extremely short or long (common mistake indicator)
    if (ratio < 0.3) {
      issues.push({
        type: 'warning',
        category: 'length',
        message: `Target is very short (${Math.round(ratio * 100)}% of source length) - possible incomplete translation`
      })
    } else if (ratio > 3) {
      issues.push({
        type: 'warning',
        category: 'length',
        message: `Target is very long (${Math.round(ratio * 100)}% of source length) - possible over-translation`
      })
    }

    return issues
  }

  /**
   * Check XML/HTML tags
   */
  private static checkTags(source: string, target: string): QAIssue[] {
    const issues: QAIssue[] = []

    // Extract tags
    const tagRegex = /<[^>]+>/g
    const sourceTags = source.match(tagRegex) || []
    const targetTags = target.match(tagRegex) || []

    // Check tag count
    if (sourceTags.length !== targetTags.length) {
      issues.push({
        type: 'error',
        category: 'tags',
        message: `Tag mismatch: Source has ${sourceTags.length} tag(s), target has ${targetTags.length}`
      })
    }

    // Check if same tags exist (order independent)
    const sourceTagSet = new Set(sourceTags)
    const targetTagSet = new Set(targetTags)

    for (const tag of sourceTagSet) {
      if (!targetTagSet.has(tag)) {
        issues.push({
          type: 'error',
          category: 'tags',
          message: `Missing tag in target: ${tag}`
        })
      }
    }

    for (const tag of targetTagSet) {
      if (!sourceTagSet.has(tag)) {
        issues.push({
          type: 'error',
          category: 'tags',
          message: `Extra tag in target: ${tag}`
        })
      }
    }

    return issues
  }

  /**
   * Check consistency (e.g., same text = same translation)
   */
  private static checkConsistency(source: string, target: string): QAIssue[] {
    const issues: QAIssue[] = []

    // Check if target is identical to source (possible forgot to translate)
    if (source.trim() === target.trim() && source.trim().length > 0) {
      issues.push({
        type: 'warning',
        category: 'consistency',
        message: 'Target is identical to source - did you forget to translate?'
      })
    }

    // Check if target contains source language characters mixed with target language
    // This is a basic check - could be enhanced with language detection
    const hasLatinChars = /[a-zA-Z]/.test(target)
    const hasCJKChars = /[\u4e00-\u9fa5\u3040-\u309f\u30a0-\u30ff]/.test(target)
    const hasCyrillicChars = /[\u0400-\u04ff]/.test(target)

    // If mixing character sets in unusual ways
    if (hasLatinChars && hasCJKChars && target.length > 20) {
      issues.push({
        type: 'info',
        category: 'consistency',
        message: 'Target contains mixed Latin and CJK characters - verify this is correct'
      })
    }

    return issues
  }

  /**
   * Get issue severity color
   */
  static getIssueColor(type: QAIssue['type']): string {
    switch (type) {
      case 'error': return 'red'
      case 'warning': return 'yellow'
      case 'info': return 'blue'
      default: return 'gray'
    }
  }

  /**
   * Get issue icon
   */
  static getIssueIcon(type: QAIssue['type']): string {
    switch (type) {
      case 'error': return '❌'
      case 'warning': return '⚠️'
      case 'info': return 'ℹ️'
      default: return '•'
    }
  }
}

export default QAChecker